import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { paymentService } from '@/services';
import { ApiError } from '@/services/api';
import { useCartStore } from '@/stores';
import { Button } from '@/components/ui';

type ReturnState = 'loading' | 'pending' | 'failed';

export function WompiReturnPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');
  const fetchCart = useCartStore((s) => s.fetchCart);
  const [state, setState] = useState<ReturnState>('loading');
  const [message, setMessage] = useState<string | null>(null);

  const transactionId = searchParams.get('id') ?? searchParams.get('transaction_id');

  useEffect(() => {
    let cancelled = false;

    async function confirmTransaction() {
      if (!transactionId) {
        setState('failed');
        setMessage(t('common.wompiMissingTransaction'));
        return;
      }

      try {
        const result = await paymentService.confirmWompi(transactionId);
        if (cancelled) return;

        if (result.orderId) {
          await fetchCart();
          await queryClient.invalidateQueries({ queryKey: ['orders'] });
          navigate(`/order-confirmation/${result.orderId}`, { replace: true });
          return;
        }

        setState('pending');
        setMessage(result.statusMessage || t('common.wompiPaymentPending'));
      } catch (err) {
        if (cancelled) return;

        if (err instanceof ApiError) {
          if (err.errorCode === 'PAYMENT_REJECTED') {
            setState('failed');
            setMessage(t('common.paymentRejected'));
            await fetchCart();
            return;
          }
          if (err.errorCode === 'PAYMENT_PENDING') {
            setState('pending');
            setMessage(t('common.wompiPaymentPending'));
            return;
          }
          setState('failed');
          setMessage(err.message || t('common.error'));
          return;
        }

        setState('failed');
        setMessage(t('common.error'));
      }
    }

    confirmTransaction();

    return () => {
      cancelled = true;
    };
  }, [fetchCart, navigate, queryClient, t, transactionId]);

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6 lg:px-8">
      <h1 className="mb-4 font-heading text-heading-2xl text-brand-950">{t('common.wompiReturnTitle')}</h1>
      <div className="rounded-lg border border-brand-700/20 bg-surface p-8 text-center">
        {state === 'loading' ? (
          <p className="mb-4 font-sans text-lg font-medium text-brand-700">
            {t('common.wompiVerifyingPayment')}
          </p>
        ) : (
          <p
            className={`mb-4 font-sans text-lg font-medium ${
              state === 'pending' ? 'text-amber-800' : 'text-red-700'
            }`}
          >
            {message}
          </p>
        )}
        <div className="flex flex-col flex-wrap justify-center gap-3 sm:flex-row">
          <Button type="button" onClick={() => navigate('/cart')}>
            {t('common.backToCart')}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/orders')}>
            {t('orders.viewMyOrders')}
          </Button>
        </div>
      </div>
    </div>
  );
}
